import type moment from "moment";
import { memo } from "react";
import { type RouteComponentProps, withRouter } from "react-router";

import { routes } from "utilities/routes";

import { RelatedItemType } from "commonComponents/entity/relatedItem/RelatedItem.types";
import { RelatedItemTag } from "commonComponents/entity/relatedItem/RelatedItemTag/RelatedItemTag";
import { useBillLinkAbsolute } from "commonComponents/utilities/Grid/bill/billLinkMode";
import { type IGridCellRenderProps } from "commonComponents/utilities/Grid/Grid.types";
import {
    GridColumnType,
    type IClientGridColumn,
} from "commonComponents/utilities/Grid/GridContainer.types";

import { LienWaiverStatusTag } from "entity/lienWaiver/common/LienWaiverStatus/LienWaiverStatus";
import { type LienWaiverStatusTypes } from "entity/lienWaiver/LienWaiver/LienWaiver.api.types";

export interface ILienWaiverStatusResponse {
    lienWaiverId: number;
    billId: number;
    jobId: number;
    status: LienWaiverStatusTypes | null;
    title: string;
    statusLastUpdatedDate: moment.Moment | undefined;
}

const LienWaiverStatusCellInternal = memo(function LienWaiverStatusCellInternal({
    cellValue,
    field,
}: IGridCellRenderProps<ILienWaiverStatusResponse> & RouteComponentProps) {
    // When true, navigate to the standalone Bill page (absolute path); when false, mount the Bill
    // modal over the current route. Read from context so the Bills list can flip modes in place.
    const useAbsoluteLink = useBillLinkAbsolute();

    if (!cellValue || cellValue.status === null) {
        return null;
    }
    const billLink = routes.bill.getDetailsLink(cellValue.billId, cellValue.jobId, "lienWaiver");
    return (
        <RelatedItemTag
            to={billLink}
            isToFullUrl={useAbsoluteLink}
            data-testid={field.full}
            itemType={RelatedItemType.BillLienWaiver}
            subject={cellValue.title}
            status={<LienWaiverStatusTag status={cellValue.status} />}
        />
    );
});

export const LienWaiverStatusCell = withRouter(LienWaiverStatusCellInternal);

// Single stable module-level column — the absolute/relative choice comes from
// BillLinkAbsoluteContext at render time (see columnBillLink for why one stable
// Cell reference matters for grid header/sort stability).
export const columnLienWaiverStatus: IClientGridColumn<ILienWaiverStatusResponse> = {
    columnType: GridColumnType.lienWaiverStatus,
    Cell: LienWaiverStatusCell,
};
